import useDeviceStore from '@/store/device';
import { storeToRefs } from 'pinia';

export const useDevice = () => {
  const deviceStore = useDeviceStore();
  const { deviceId, deviceToken, hasDeviceId, hasDeviceToken } = storeToRefs(deviceStore);
  const { resetDevice } = deviceStore;

  const { pending: isRegistering, execute: register } = useSubmit();
  const { pending: isUnregistering, execute: unregister } = useSubmit();

  const registerDevice = async (token: string, id: string): Promise<void> => {
    if (!token || !id || hasDevice.value || isRegistering.value) return;

    isRegistering.value = true;

    await register('/devices', {
      method: 'POST',
      body: {
        device_id: id,
        device_token: token,
      },
    });

    deviceId.value = id;
    deviceToken.value = token;

    isRegistering.value = false;
  };

  const unregisterDevice = async (): Promise<void> => {
    if (isUnregistering.value) return;
    isUnregistering.value = true;

    await unregister('/devices', {
      method: 'DELETE',
      body: {
        device_id: deviceId.value,
        device_token: deviceToken.value,
      },
    });

    resetDevice();

    isUnregistering.value = false;
  };

  const hasDevice = computed((): boolean => {
    return hasDeviceId.value && hasDeviceToken.value;
  });

  const getOS = () => {
    const userAgent = import.meta.server
      ? useRequestHeaders(['user-agent'])['user-agent'] || ''
      : window.navigator.userAgent;

    // Check for iOS:
    // 1. Standard UA check for iPhone/iPad/iPod
    // 2. Optional: Add 'maxTouchPoints' check to catch modern iPads (iPadOS)
    const isIOS = /iPad|iPhone|iPod/.test(userAgent) && !('MSStream' in (import.meta.client ? window : {}));

    // For modern iPads (iPadOS 13+) that identify as "Macintosh"
    const isIPadOS = import.meta.client && userAgent.includes('Macintosh') && navigator.maxTouchPoints > 1;

    return {
      isAndroid: /android/i.test(userAgent),
      isIOS: isIOS || isIPadOS,
    };
  };

  const os = getOS();

  return {
    hasDevice,

    registerDevice,
    unregisterDevice,

    isAndroid: os.isAndroid,
    isIOS: os.isIOS,
    os,
  };
};
